import "@mdxeditor/editor/style.css";

import {
  BoldItalicUnderlineToggles,
  headingsPlugin,
  linkPlugin,
  listsPlugin,
  ListsToggle,
  markdownShortcutPlugin,
  MDXEditor,
  MDXEditorMethods,
  quotePlugin,
  tablePlugin,
  thematicBreakPlugin,
  toolbarPlugin,
  UndoRedo,
} from "@mdxeditor/editor";
import { Box, FormControl, FormHelperText, SxProps } from "@mui/material";
import { useEffect, useRef } from "react";
import { Controller, get, useFormState, useWatch } from "react-hook-form";

import {
  AdditionalFormFieldProps,
  BaseFormFieldDefinition,
  FormFieldValidationDefinition,
} from "../form-field";

export const RICH_TEXT_FORM_FIELD_TYPE = "rich-text" as const;

const ToolbarContents = () => (
  <>
    <UndoRedo />
    <BoldItalicUnderlineToggles />
    <ListsToggle />
  </>
);

export type RichTextFormFieldDefinition = BaseFormFieldDefinition<
  typeof RICH_TEXT_FORM_FIELD_TYPE
> & {
  type: typeof RICH_TEXT_FORM_FIELD_TYPE;
  maxLength?: FormFieldValidationDefinition<number>;
  minLength?: FormFieldValidationDefinition<number>;
  multiline?: boolean;
  pattern?: FormFieldValidationDefinition<RegExp>;
  sx?: SxProps;
};

export function RichTextFormField({
  disabled = false,
  errorMessage,
  maxLength,
  minLength,
  name,
  pattern,
  required,
}: RichTextFormFieldDefinition & AdditionalFormFieldProps) {
  const { errors } = useFormState();

  const error = get(errors, name);
  const mdxEditorRef = useRef<MDXEditorMethods>(null);

  // TODO: When opening an initiative after editing a different one, the markdown doesnt get set to the new initiaties' markdown
  // Maybe a better solution?
  const value: string = useWatch({ name });
  useEffect(
    () => mdxEditorRef?.current?.setMarkdown(value ?? ""),
    [value, mdxEditorRef]
  );

  return (
    <FormControl sx={{ height: "100%" }} fullWidth>
      <Controller
        name={name}
        disabled={disabled}
        rules={{
          maxLength,
          minLength,
          pattern,
          required,
          // RHF's required passes for whitespace-only strings like "\n" from MDXEditor
          validate: required?.value
            ? (v: string | null | undefined) =>
                Boolean(v?.trim()) || required.message
            : undefined,
        }}
        render={({ field }) => (
          <Box
            data-testid={`rich-text-field-${name}`}
            onBlurCapture={field.onBlur}
            sx={(theme) => ({
              padding: "5px",
              border: `1px solid ${
                error ? theme.palette.error.main : "#0000003b"
              }`,

              borderRadius: "5px",
              backgroundColor: "#fff",
              '& [class*="toolbarRoot"]': {
                position: "static !important",
                top: "auto !important",
                backgroundColor: "white",
                zIndex: "auto",
              },
            })}
          >
            <MDXEditor
              ref={mdxEditorRef}
              readOnly={disabled}
              markdown={field.value ?? ""}
              onChange={field.onChange}
              plugins={[
                toolbarPlugin({
                  toolbarContents: ToolbarContents,
                }),
                headingsPlugin(),
                listsPlugin(),
                quotePlugin(),
                thematicBreakPlugin(),
                linkPlugin(),
                tablePlugin(),
                markdownShortcutPlugin(),
              ]}
            />
            {error && (
              <FormHelperText
                sx={(theme) => ({ color: theme.palette.error.main })}
              >
                {errorMessage}
              </FormHelperText>
            )}
          </Box>
        )}
      />
    </FormControl>
  );
}
